import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import axios from "../../../api/axios";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DataTable from "examples/Tables/DataTable";

function LoanActionCell({ row, onStop, onComplete }) {
  return (
    <MDBox display="flex" gap={1} flexWrap="wrap">
      <MDButton
        size="small"
        color="warning"
        disabled={row.original.status !== "active"}
        onClick={() => onStop(row.original)}
      >
        Stop
      </MDButton>

      <MDButton
        size="small"
        color="success"
        disabled={row.original.status === "completed"}
        onClick={() => onComplete(row.original)}
      >
        Complete
      </MDButton>
    </MDBox>
  );
}

LoanActionCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      status: PropTypes.string,
    }).isRequired,
  }).isRequired,
  onStop: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
};

export default function Loans() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    pfno: "",
    status: "",
  });

  const [form, setForm] = useState({
    pfno: "",
    loan_code: "",
    loan_name: "",
    principal_amount: "",
    monthly_deduction: "",
    balance_amount: "",
    start_date: "",
    end_date: "",
    remarks: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/pay-items/loans", {
        params: {
          pfno: filters.pfno || undefined,
          status: filters.status || undefined,
          per_page: 200,
        },
      });
      setRows(res.data?.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    await axios.post("/pay-items/loans", {
      ...form,
      principal_amount: Number(form.principal_amount || 0),
      monthly_deduction: Number(form.monthly_deduction || 0),
      balance_amount: form.balance_amount ? Number(form.balance_amount) : null,
      end_date: form.end_date || null,
    });

    setForm({
      pfno: "",
      loan_code: "",
      loan_name: "",
      principal_amount: "",
      monthly_deduction: "",
      balance_amount: "",
      start_date: "",
      end_date: "",
      remarks: "",
    });

    await load();
  };

  const stopLoan = async (row) => {
    await axios.post(`/pay-items/loans/${row.id}/stop`, { remarks: "Stopped from UI" });
    await load();
  };

  const completeLoan = async (row) => {
    await axios.post(`/pay-items/loans/${row.id}/complete`, { remarks: "Completed from UI" });
    await load();
  };

  const columns = useMemo(
    () => [
      { Header: "PFNO", accessor: "pfno" },
      { Header: "Code", accessor: "loan_code" },
      { Header: "Name", accessor: "loan_name" },
      { Header: "Principal", accessor: "principal_amount" },
      { Header: "Monthly Ded.", accessor: "monthly_deduction" },
      { Header: "Balance", accessor: "balance_amount" },
      { Header: "Start", accessor: "start_date" },
      { Header: "End", accessor: "end_date" },
      { Header: "Status", accessor: "status" },
      {
        Header: "Actions",
        accessor: "actions",
        Cell: (cellProps) => (
          <LoanActionCell row={cellProps.row} onStop={stopLoan} onComplete={completeLoan} />
        ),
      },
    ],
    []
  );

  return (
    <MDBox p={3}>
      <MDBox
        mb={3}
        p={3}
        borderRadius="xl"
        sx={{
          background: "linear-gradient(135deg, #bf360c 0%, #d84315 50%, #ff8a65 100%)",
          color: "#fff",
          boxShadow: "0 12px 30px rgba(191,54,12,0.20)",
        }}
      >
        <MDTypography variant="h4" color="white">
          Staff Loans
        </MDTypography>
        <MDTypography variant="button" color="white">
          Manage loan masters and protected payroll recovery schedules.
        </MDTypography>
      </MDBox>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={4}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6">Create Loan</MDTypography>

              <MDBox mt={2} display="flex" flexDirection="column" gap={2}>
                <MDInput
                  label="PFNO"
                  value={form.pfno}
                  onChange={(e) => setForm({ ...form, pfno: e.target.value })}
                />
                <MDInput
                  label="Loan Code"
                  value={form.loan_code}
                  onChange={(e) => setForm({ ...form, loan_code: e.target.value })}
                />
                <MDInput
                  label="Loan Name"
                  value={form.loan_name}
                  onChange={(e) => setForm({ ...form, loan_name: e.target.value })}
                />
                <MDInput
                  label="Principal Amount"
                  type="number"
                  value={form.principal_amount}
                  onChange={(e) => setForm({ ...form, principal_amount: e.target.value })}
                />
                <MDInput
                  label="Monthly Deduction"
                  type="number"
                  value={form.monthly_deduction}
                  onChange={(e) => setForm({ ...form, monthly_deduction: e.target.value })}
                />
                <MDInput
                  label="Balance Amount"
                  type="number"
                  value={form.balance_amount}
                  onChange={(e) => setForm({ ...form, balance_amount: e.target.value })}
                />
                <MDInput
                  label="Start Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                />
                <MDInput
                  label="End Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                />
                <MDInput
                  label="Remarks"
                  value={form.remarks}
                  onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                />

                <MDButton color="info" onClick={create} disabled={loading}>
                  Save Loan
                </MDButton>
              </MDBox>
            </MDBox>
          </Card>
        </Grid>

        <Grid item xs={12} lg={8}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6">Loan Register</MDTypography>

              <MDBox mt={2} display="flex" gap={2} flexWrap="wrap">
                <MDBox width={180}>
                  <MDInput
                    label="Filter PFNO"
                    value={filters.pfno}
                    onChange={(e) => setFilters({ ...filters, pfno: e.target.value })}
                  />
                </MDBox>

                <MDBox width={160}>
                  <MDInput
                    label="Status"
                    value={filters.status}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  />
                </MDBox>

                <MDButton color="secondary" onClick={load} disabled={loading}>
                  Load
                </MDButton>
              </MDBox>

              <MDBox mt={2}>
                <DataTable
                  table={{ columns, rows }}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  canSearch
                  pagination
                  noEndBorder
                />
              </MDBox>
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
}
